import { connect } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { ValueOfRecord } from 'ramda';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DecisionActSubject } from 'DecisionPackage/DecisionAct/DecisionPaperlessActItem/DecisionActSubject/DecisionActSubject';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IDecisionResultDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionResultDecisionForm';
import { DecisionSubjectItem } from 'DecisionPackage/DecisionSubjectModule/components/DecisionSubjectItem';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { DecisionActSubjectInfo } from 'DecisionPackage/DecisionAct/DecisionPaperlessActItem/DecisionActSubject/components/DecisionActSubjectInfo';
import { getDecisionTargetEntitySelectOptions } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { KnmResultItemSubjectTypesEnum } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultItemSubjectInfo';

import { resultDecisionSubjectTypeOptions } from '../DecisionsResultList';
import styles from './DecisionResultItemContentSubject.module.scss';

interface IDecisionResultItemContentSubjectProps {
  organization?: IDecisionOrganizationForm;
  name?: string;
  resultDecisionId: string;
  isWatchOnly: boolean;
  isLater4111ForDecision?: boolean;
  value?: IDecisionResultDecisionForm;
  formRef?: IDecisionFormRef;
}

interface IDecisionResultItemContentSubjectStoreProps {
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}

const DecisionResultItemContentSubjectWithoutStore = ({
  organization,
  name,
  isLater4111ForDecision,
  subjectTypeOptions,
  value,
  formRef,
  isWatchOnly,
  resultDecisionId,
}: IDecisionResultItemContentSubjectProps & IDecisionResultItemContentSubjectStoreProps) => {
  const isControlledPerson = value?.subjectType?.value === KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT;

  const getOrganizationBlockId = (id: string) => {
    if (id.includes('founder')) {
      const founderPath = `founder${id.split('founder').pop()}`;

      return `organization.${resultDecisionId}.${founderPath}`;
    }

    return `organization.${resultDecisionId}.founder.${id}`;
  };

  const handleChangeSubjectType = useCallback(() => {
    formRef?.current.change(
      `${name}.founder` as keyof IDecisionForm,
      {
        organizationType: subjectTypeOptions?.find(item => item.label === KnmSubjectTypesEnum.UR),
      } as ValueOfRecord<IDecisionForm>,
    );
  }, [formRef, name]);

  const isUR = useMemo(() => {
    return organization?.organizationType?.label === KnmSubjectTypesEnum.UR;
  }, [organization]);

  return organization?.organizationType?.code &&
    isLater4111ForDecision &&
    organization.organizationType.code === KnmResultItemSubjectTypesEnum.UR ? (
    <div className={styles.Wrapper}>
      <Row>
        <FieldBlock
          label="Лицо, в отношении которого принято решение"
          size="half"
          id={`organization.${resultDecisionId}.subjectType`}
        >
          <SelectInputField
            name={`${name}.subjectType`}
            disabled={isWatchOnly || !isUR}
            isClearable={false}
            options={resultDecisionSubjectTypeOptions}
            onChange={handleChangeSubjectType}
          />
        </FieldBlock>
      </Row>
      <KnmCollapse
        id={`${name}.organizationBlock`}
        theme="fields"
        title={isControlledPerson ? 'Сведения о контролируемом лице' : 'Сведения об учредителе'}
        openByDefault
      >
        <>
          {isControlledPerson && (
            <Row>
              <DecisionActSubjectInfo
                name={`organization`}
                isAutocomplete={organization?.isAutocomplete}
                inn={organization?.inn}
                ogrn={organization?.ogrn}
                subjectType={organization?.organizationType}
                kpp={organization?.kpp}
                smevRequestStatus={organization?.smevRequestStatus}
                rafpNza={organization?.rafpNza}
                snils={organization?.snils}
                checkSubjectResult={organization?.checkSubjectResult || null}
                ervkId={organization?.ervkId}
                isDecision
              />
            </Row>
          )}
          {!isControlledPerson && (
            <DecisionSubjectItem
              ervkId={value?.founder?.ervkId}
              checkSubjectResult={value?.founder?.checkSubjectResult}
              formRef={formRef as IKnmFormRef}
              isWatchOnly={isWatchOnly}
              getBlockId={getOrganizationBlockId}
              inn={value?.founder?.inn}
              isFiz={false}
              isIt={value?.founder?.isPresentInItRegistry}
              isSonko={value?.founder?.isSonko}
              itAccreditationResponse={value?.founder?.itAccreditationResponse}
              kpp={value?.founder?.kpp}
              mspCode={value?.founder?.mspCode}
              mspRegistryDate={value?.founder?.mspRegistryDate}
              name={`${name}.founder`}
              ogrn={value?.founder?.ogrn}
              organizationType={value?.founder?.organizationType}
              rafpNza={value?.founder?.rafpNza}
              smevRequestStatus={value?.founder?.smevRequestStatus}
              snils={value?.founder?.snils}
              subjectTypeOptions={subjectTypeOptions}
              subjectTypeLabel="Тип учредителя"
              organizationAutocomplete={value?.founder?.isAutocomplete}
              smevItAccreditationRequestStatus={value?.founder?.smevItAccreditationRequestStatus}
            />
          )}
        </>
      </KnmCollapse>
    </div>
  ) : (
    <DecisionActSubject organization={organization} isDecision />
  );
};

const mapStateToProps = (state: IRootState): IDecisionResultItemContentSubjectStoreProps => ({
  subjectTypeOptions: getDecisionTargetEntitySelectOptions(state),
});

export const DecisionResultItemContentSubject = connect(mapStateToProps)(DecisionResultItemContentSubjectWithoutStore);
